import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import './EquityNew.css';

// ── pure date helper (no state dependency) ────────────────────────────────────

const parseDate = (s) => {
  if (!s) return null;
  // TastyTrade / ISO: YYYY-MM-DD...
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  // IBKR: DD/MM/YY
  const p = s.split('/');
  if (p.length === 3) {
    const [dd, mm, yy] = p.map(Number);
    return new Date(yy < 50 ? 2000 + yy : 1900 + yy, mm - 1, dd);
  }
  return null;
};

const toDateKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_CHART = { data: [], yearEndNormal: null, yearEndStretch: null };

function EquityNew({ activeSubmenu, onLogout }) {
  const submenu = activeSubmenu || 'equity';

  // ── filters ────────────────────────────────────────────────────────────────
  const [selectedBroker, setSelectedBroker] = useState('all');
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [groupBy, setGroupBy] = useState('day');

  // ── data ───────────────────────────────────────────────────────────────────
  const [accounts, setAccounts] = useState([]);
  const [userSettings, setUserSettings] = useState(null);
  // Equity for each broker — always fetched separately to avoid date-format collisions
  const [ibkrEquity, setIbkrEquity] = useState([]);
  const [tastyEquity, setTastyEquity] = useState([]);
  // Trades for P&L tab
  const [ibkrTrades, setIbkrTrades] = useState([]);
  const [tastyTrades, setTastyTrades] = useState([]);

  const [equityLoading, setEquityLoading] = useState(false);
  const [pnlLoading, setPnlLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── currency ───────────────────────────────────────────────────────────────
  const [displayCurrency, setDisplayCurrency] = useState('EUR');
  const [eurToUsdRate, setEurToUsdRate] = useState(null);

  // ── period helpers ─────────────────────────────────────────────────────────
  const getPeriodKey = useCallback((date) => {
    if (!date) return null;
    if (groupBy === 'day') return toDateKey(date);
    if (groupBy === 'week') {
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const day = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - day);
      const ys = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      const wk = Math.ceil((((d - ys) / 86400000) + 1) / 7);
      return `${d.getUTCFullYear()}-W${String(wk).padStart(2, '0')}`;
    }
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }, [groupBy]);

  const formatPeriodLabel = useCallback((key, date) => {
    if (!date) return key;
    if (groupBy === 'day')
      return date.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    if (groupBy === 'week') {
      const mon = new Date(date);
      const wd = mon.getDay() || 7;
      mon.setDate(mon.getDate() - wd + 1);
      const sun = new Date(mon);
      sun.setDate(mon.getDate() + 6);
      const f = (d) => d.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' });
      return `${f(mon)}–${f(sun)}`;
    }
    return date.toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' });
  }, [groupBy]);

  // ── account helpers ────────────────────────────────────────────────────────
  const selectedAccountObj = useMemo(
    () => accounts.find(a => a.id === parseInt(selectedAccount)) || null,
    [accounts, selectedAccount]
  );

  const filteredAccounts = useMemo(
    () => accounts.filter(a => selectedBroker === 'all' || a.broker === selectedBroker),
    [accounts, selectedBroker]
  );

  // ── fetch helpers ──────────────────────────────────────────────────────────
  const fetchAccounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/portfolio/accounts/?page_size=all`);
      const data = res.data.results || res.data;
      setAccounts(Array.isArray(data) ? data : []);
    } catch (err) { console.error(err); }
  }, []);

  const fetchUserSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/portfolio/user-settings/`);
      const data = res.data.results || res.data;
      const settings = Array.isArray(data) ? data[0] : data;
      setUserSettings(settings || null);
    } catch (err) { console.error(err); }
  }, []);

  const fetchFxRate = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/portfolio/equity/fx-rate/`);
      if (res.data.eur_to_usd) setEurToUsdRate(res.data.eur_to_usd);
    } catch (err) { console.error('Could not fetch FX rate', err); }
  }, []);

  // Always fetch each broker separately — mixing them in one call causes date-format
  // collisions (IBKR uses DD/MM/YY, TastyTrade YYYY-MM-DD) that the backend can't
  // group correctly, leading to duplicate period rows that overwrite each other.
  const fetchEquityData = useCallback(async () => {
    try {
      setEquityLoading(true);
      setError(null);

      const ibkrParams = ['broker=ibkr'];
      const tastyParams = ['broker=tastytrade'];

      // Apply account filter only to the matching broker
      if (selectedAccountObj?.broker === 'ibkr')
        ibkrParams.push(`account_id=${selectedAccountObj.broker_account_id}`);
      if (selectedAccountObj?.broker === 'tastytrade')
        tastyParams.push(`account_id=${selectedAccountObj.broker_account_id}`);

      const [ibkrRes, tastyRes] = await Promise.all([
        axios.get(`${API_URL}/portfolio/equity/?${ibkrParams.join('&')}`),
        axios.get(`${API_URL}/portfolio/equity/?${tastyParams.join('&')}`),
      ]);
      setIbkrEquity(ibkrRes.data.results || ibkrRes.data || []);
      setTastyEquity(tastyRes.data.results || tastyRes.data || []);
    } catch (err) {
      if (err.response?.status === 401) { onLogout?.(); return; }
      setError('Error loading equity: ' + err.message);
    } finally {
      setEquityLoading(false);
    }
  }, [selectedAccountObj, onLogout]);

  const fetchPnlTrades = useCallback(async () => {
    try {
      setPnlLoading(true);
      const accountParam = selectedAccountObj?.broker_account_id;
      const buildUrl = (broker) => {
        const p = [`broker=${broker}`, 'page_size=all'];
        if (accountParam && selectedAccountObj?.broker === broker)
          p.push(`account_id=${accountParam}`);
        return `${API_URL}/portfolio/trades/?${p.join('&')}`;
      };
      const [ir, tr] = await Promise.all([
        axios.get(buildUrl('ibkr')),
        axios.get(buildUrl('tastytrade')),
      ]);
      setIbkrTrades(ir.data.results || ir.data || []);
      setTastyTrades(tr.data.results || tr.data || []);
    } catch (err) {
      if (err.response?.status === 401) { onLogout?.(); return; }
      console.error(err);
    } finally {
      setPnlLoading(false);
    }
  }, [selectedAccountObj, onLogout]);

  // ── effects ────────────────────────────────────────────────────────────────
  useEffect(() => { fetchAccounts(); fetchUserSettings(); fetchFxRate(); }, [fetchAccounts, fetchUserSettings, fetchFxRate]);

  useEffect(() => {
    if (submenu === 'equity') fetchEquityData();
  }, [submenu, fetchEquityData]);

  useEffect(() => {
    if (submenu === 'pnl') fetchPnlTrades();
  }, [submenu, fetchPnlTrades]);

  // ── equity grouping helper ─────────────────────────────────────────────────
  // Groups one broker's raw equity rows by period, taking the LATEST value per period.
  // Returns Map<periodKey, {date, cash, stocks, options, total}>
  const groupEquityByPeriod = useCallback((rows) => {
    const map = new Map();
    rows.forEach(item => {
      const d = parseDate(item.report_date);
      if (!d) return;
      const key = getPeriodKey(d);
      if (!key) return;
      const ex = map.get(key);
      if (!ex || d > ex.date) {
        map.set(key, {
          date: d,
          cash: parseFloat(item.cash) || 0,
          stocks: parseFloat(item.stock) || 0,
          options: parseFloat(item.options) || 0,
          total: parseFloat(item.total) || 0,
        });
      }
    });
    return map;
  }, [getPeriodKey]);

  // ── equity table data ──────────────────────────────────────────────────────
  const groupedEquity = useMemo(() => {
    const ibkrMap = groupEquityByPeriod(ibkrEquity);
    const tastyMap = groupEquityByPeriod(tastyEquity);

    // Determine which brokers to include
    const useIbkr = selectedBroker === 'all' || selectedBroker === 'ibkr';
    const useTasty = selectedBroker === 'all' || selectedBroker === 'tastytrade';

    // Collect all period keys
    const allKeys = new Set([
      ...(useIbkr ? ibkrMap.keys() : []),
      ...(useTasty ? tastyMap.keys() : []),
    ]);

    const rows = [];
    allKeys.forEach(key => {
      const ibkr = useIbkr ? ibkrMap.get(key) : null;
      const tasty = useTasty ? tastyMap.get(key) : null;
      // Use the latest date for display label
      const date = ibkr && tasty
        ? (ibkr.date > tasty.date ? ibkr.date : tasty.date)
        : (ibkr || tasty).date;
      rows.push({
        key,
        date,
        label: formatPeriodLabel(key, date),
        cash: (ibkr?.cash || 0) + (tasty?.cash || 0),
        stocks: (ibkr?.stocks || 0) + (tasty?.stocks || 0),
        options: (ibkr?.options || 0) + (tasty?.options || 0),
        total: (ibkr?.total || 0) + (tasty?.total || 0),
      });
    });

    rows.sort((a, b) => b.date - a.date);

    return rows.map((row, i) => {
      const prev = rows[i + 1];
      return {
        ...row,
        totalDiff: prev ? row.total - prev.total : null,
        stocksDiff: prev ? row.stocks - prev.stocks : null,
        optionsDiff: prev ? row.options - prev.options : null,
      };
    });
  }, [ibkrEquity, tastyEquity, selectedBroker, groupEquityByPeriod, formatPeriodLabel]);

  // ── equity chart data — always day granularity, independent of groupBy ───────
  // settings: UserSettings object for goal lines
  // useIbkrBase: true = use yearly_start_equity + Jan 1 as goal baseline (IBKR);
  //              false = use first data point as baseline (TastyTrade)
  const buildChartData = useCallback((rows, ytdOnly, settings, useIbkrBase) => {
    if (!rows.length) return EMPTY_CHART;
    const yearStart = new Date(new Date().getFullYear(), 0, 1);
    const map = new Map();
    rows.forEach(item => {
      const d = parseDate(item.report_date);
      if (!d) return;
      if (ytdOnly && d < yearStart) return;
      const total = parseFloat(item.total) || 0;
      if (total === 0) return;
      const key = toDateKey(d);
      const ex = map.get(key);
      if (!ex || d > ex.date) map.set(key, { date: d, value: total });
    });

    const sorted = Array.from(map.values()).sort((a, b) => a.date - b.date);
    if (!sorted.length) return EMPTY_CHART;

    const firstDataDate = sorted[0].date;
    const firstDataValue = sorted[0].value;

    // Goal baseline: IBKR uses Jan 1 + yearly_start_equity; TastyTrade uses first data point
    const goalStartDate = useIbkrBase ? yearStart : firstDataDate;
    const goalStartValue = (useIbkrBase && settings?.yearly_start_equity)
      ? parseFloat(settings.yearly_start_equity)
      : firstDataValue;

    // Year end (Dec 31 of the chart's starting year)
    const yearEnd = new Date(goalStartDate.getFullYear(), 11, 31);
    const totalDays = (yearEnd - goalStartDate) / 86400000;

    // Resolve goal amounts from settings (handle both amount and percentage types).
    // Always use goalStartValue (account-specific) as the percentage base — NOT
    // settings.yearly_start_equity, which is the IBKR/global equity and would
    // produce wildly incorrect goal amounts for the TastyTrade chart.
    let normalGoalAmount = null;
    let stretchGoalAmount = null;
    if (settings) {
      if (settings.yearly_goal_type === 'percentage' && settings.yearly_pnl_goal_percentage) {
        normalGoalAmount = goalStartValue * parseFloat(settings.yearly_pnl_goal_percentage) / 100;
      } else if (settings.yearly_pnl_goal) {
        normalGoalAmount = parseFloat(settings.yearly_pnl_goal);
      }
      if (settings.yearly_stretch_goal_type === 'percentage' && settings.yearly_stretch_goal_percentage) {
        stretchGoalAmount = goalStartValue * parseFloat(settings.yearly_stretch_goal_percentage) / 100;
      } else if (settings.yearly_stretch_goal) {
        stretchGoalAmount = parseFloat(settings.yearly_stretch_goal);
      }
    }

    // Linear interpolation: equity at date if we're on track for the goal
    const goalAt = (date, amount) => {
      if (!amount) return undefined;
      const days = Math.max(0, (date - goalStartDate) / 86400000);
      const frac = totalDays > 0 ? Math.min(1, days / totalDays) : 0;
      return goalStartValue + frac * amount;
    };

    const data = sorted.map(r => ({
      label: r.date.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' }),
      date: r.date,
      value: r.value,
      normalGoal: goalAt(r.date, normalGoalAmount),
      stretchGoal: goalAt(r.date, stretchGoalAmount),
    }));

    // Return year-end targets alongside data so the chart can display them as
    // text without adding a Dec 31 data point (which distorts the Y-axis domain).
    return {
      data,
      yearEndNormal: normalGoalAmount != null ? goalStartValue + normalGoalAmount : null,
      yearEndStretch: stretchGoalAmount != null ? goalStartValue + stretchGoalAmount : null,
    };
  }, []);

  const ibkrChart = useMemo(
    () => buildChartData(ibkrEquity, true, userSettings, true),
    [ibkrEquity, userSettings, buildChartData]
  );
  const tastyChart = useMemo(
    () => buildChartData(tastyEquity, false, userSettings, false),
    [tastyEquity, userSettings, buildChartData]
  );

  // ── IBKR P&L (fifo_pnl_realized is correct for IBKR) ─────────────────────
  const ibkrPnl = useMemo(() => {
    const map = new Map();
    ibkrTrades.forEach(t => {
      const pnl = parseFloat(t.fifo_pnl_realized);
      if (!pnl || isNaN(pnl)) return;
      const d = parseDate(t.trade_date);
      if (!d) return;
      const key = getPeriodKey(d);
      if (!key) return;
      const ex = map.get(key);
      if (ex) { ex.pnl += pnl; if (d > ex.date) ex.date = d; }
      else map.set(key, { key, date: d, pnl });
    });
    const rows = Array.from(map.values()).sort((a, b) => b.date - a.date);
    let cum = 0;
    [...rows].reverse().forEach(r => { cum += r.pnl; r.cumulative = cum; });
    return rows.map(r => ({ ...r, label: formatPeriodLabel(r.key, r.date) }));
  }, [ibkrTrades, getPeriodKey, formatPeriodLabel]);

  // ── TastyTrade P&L — balanced round-trips via net_cash ────────────────────
  //
  // Problem: TastyTrade's import stores net SALE PROCEEDS (e.g. $10,180) in
  // fifo_pnl_realized instead of actual profit (~$205). We compute true P&L
  // using net_cash of ALL trades, but only for conids that are FULLY closed
  // (total opened qty ≈ total closed qty). Partially-closed positions (like MSTR,
  // where old shares were sold but new ones opened simultaneously) are skipped
  // because we lack the original cost basis.
  const tastyPnl = useMemo(() => {
    // Group by conid
    const byConid = {};
    tastyTrades.forEach(t => {
      const conid = t.conid || t.symbol;
      if (!conid) return;
      if (!byConid[conid]) byConid[conid] = { opens: [], closes: [] };
      if (t.open_close_indicator === 'O') byConid[conid].opens.push(t);
      else if (t.open_close_indicator === 'C') byConid[conid].closes.push(t);
    });

    const events = [];

    Object.values(byConid).forEach(({ opens, closes }) => {
      if (!opens.length || !closes.length) return;

      const totalOpenQty = opens.reduce((s, t) => s + (parseFloat(t.quantity) || 0), 0);
      const totalCloseQty = closes.reduce((s, t) => s + (parseFloat(t.quantity) || 0), 0);

      // Only process fully balanced round-trips (all opened qty was also closed)
      if (Math.abs(totalOpenQty - totalCloseQty) > 0.01) return;

      // P&L = sum of all net_cash (opens negative + closes positive = true profit)
      const pnl = [...opens, ...closes]
        .reduce((s, t) => s + (parseFloat(t.net_cash) || 0), 0);

      // Attribute to date of the last closing trade
      const lastClose = closes.reduce((best, t) => {
        const d = parseDate(t.trade_date);
        const bd = parseDate(best.trade_date);
        return d && (!bd || d > bd) ? t : best;
      });
      const d = parseDate(lastClose.trade_date);
      if (d) events.push({ date: d, pnl });
    });

    // Group by period
    const map = new Map();
    events.forEach(({ date, pnl }) => {
      const key = getPeriodKey(date);
      if (!key) return;
      const ex = map.get(key);
      if (ex) { ex.pnl += pnl; if (date > ex.date) ex.date = date; }
      else map.set(key, { key, date, pnl });
    });

    const rows = Array.from(map.values()).sort((a, b) => b.date - a.date);
    let cum = 0;
    [...rows].reverse().forEach(r => { cum += r.pnl; r.cumulative = cum; });
    return rows.map(r => ({ ...r, label: formatPeriodLabel(r.key, r.date) }));
  }, [tastyTrades, getPeriodKey, formatPeriodLabel]);

  // ── format helpers ─────────────────────────────────────────────────────────
  // Backend always returns EUR (TastyTrade USD is converted server-side)
  const currencySymbol = displayCurrency === 'USD' ? '$' : '€';
  const currencyMultiplier = displayCurrency === 'USD' && eurToUsdRate ? eurToUsdRate : 1;
  const fmt = (v) => {
    if (v == null || isNaN(v)) return '—';
    const n = parseFloat(v) * currencyMultiplier;
    return `${currencySymbol}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const fmtDiff = (v) => {
    if (v == null || isNaN(v)) return '—';
    const n = parseFloat(v) * currencyMultiplier;
    const abs = `${currencySymbol}${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return n >= 0 ? `+${abs}` : `-${abs}`;
  };
  // Adaptive precision: 2 decimal k for <10k, 1 for <100k, 0 above
  const fmtAxis = (v) => {
    const cv = v * currencyMultiplier;
    if (Math.abs(cv) >= 1000) {
      const k = cv / 1000;
      const dec = k >= 100 ? 0 : k >= 10 ? 1 : 2;
      return `${currencySymbol}${k.toFixed(dec)}k`;
    }
    return `${currencySymbol}${cv.toFixed(0)}`;
  };
  const dc = (v) => (v == null ? '' : v >= 0 ? 'en-positive' : 'en-negative');

  // ── render: controls bar ───────────────────────────────────────────────────
  const controls = (
    <div className="en-controls">
      <div className="en-control-group">
        <label>Broker:</label>
        <select value={selectedBroker}
          onChange={e => { setSelectedBroker(e.target.value); setSelectedAccount('all'); }}
          className="en-select">
          <option value="all">All brokers</option>
          <option value="ibkr">IBKR</option>
          <option value="tastytrade">TastyTrade</option>
        </select>
      </div>
      <div className="en-control-group">
        <label>Account:</label>
        <select value={selectedAccount} onChange={e => setSelectedAccount(e.target.value)} className="en-select">
          <option value="all">All accounts</option>
          {filteredAccounts.map(a => (
            <option key={a.id} value={a.id}>{a.display_name || a.broker_account_id}</option>
          ))}
        </select>
      </div>
      <div className="en-control-group">
        <label>Group by:</label>
        <div className="en-toggle-group">
          {['day', 'week', 'month'].map(v => (
            <button key={v} className={groupBy === v ? 'active' : ''} onClick={() => setGroupBy(v)}>
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div className="en-control-group">
        <label>Currency:</label>
        <div className="en-toggle-group">
          <button
            className={displayCurrency === 'EUR' ? 'active' : ''}
            onClick={() => setDisplayCurrency('EUR')}
          >
            € EUR
          </button>
          <button
            className={displayCurrency === 'USD' ? 'active' : ''}
            onClick={() => setDisplayCurrency('USD')}
            disabled={!eurToUsdRate}
            title={eurToUsdRate ? `Rate: 1 EUR = ${eurToUsdRate.toFixed(4)} USD` : 'FX rate unavailable'}
          >
            $ USD
          </button>
        </div>
      </div>
    </div>
  );

  // ── render: equity chart ─────────────────────────────────────────────────────
  const renderChart = (chartObj, title, color) => {
    const { data, yearEndNormal, yearEndStretch } = chartObj || EMPTY_CHART;

    if (!data || !data.length) return (
      <div className="en-chart-panel">
        <div className="en-chart-header"><span className="en-chart-title">{title}</span></div>
        <div className="en-chart-empty">No data</div>
      </div>
    );

    const equityVals = data.map(d => d.value).filter(v => v != null);
    const startValue = equityVals[0];
    const mn = Math.min(...equityVals);
    const mx = Math.max(...equityVals);

    // Y-axis domain: pad by 2% of range on each side, minimum 1% of start value so
    // a perfectly flat line doesn't sit on the axis edge.
    const range = mx - mn;
    const minPad = (startValue || 1) * 0.01;
    const pad = Math.max(range * 0.2, minPad);
    const yMin = mn - pad;
    const yMax = mx + pad;

    const hasNormalGoal  = data.some(d => d.normalGoal  != null);
    const hasStretchGoal = data.some(d => d.stretchGoal != null);

    // On-track: compare the last actual equity point vs its goal pace value
    const lastPoint = [...data].reverse().find(d => d.value != null);
    const onTrackNormal  = hasNormalGoal  && lastPoint?.normalGoal  != null
      ? lastPoint.value >= lastPoint.normalGoal  : null;
    const onTrackStretch = hasStretchGoal && lastPoint?.stretchGoal != null
      ? lastPoint.value >= lastPoint.stretchGoal : null;

    // Only show red start line when equity has actually fallen below start
    const belowStart = equityVals.some(v => v < startValue);

    const tooltipFmt = (v, name) => {
      if (v == null) return [null, null];
      const labels = { value: 'Equity', normalGoal: 'Goal pace', stretchGoal: 'Stretch pace' };
      return [fmt(v), labels[name] || name];
    };

    return (
      <div className="en-chart-panel">
        {/* header row */}
        <div className="en-chart-header">
          <span className="en-chart-title">{title}</span>
          <div className="en-chart-header-right">
            {onTrackNormal  !== null && (
              <span className={`en-goal-badge ${onTrackNormal  ? 'en-goal-badge--on' : 'en-goal-badge--off'}`}>
                {onTrackNormal  ? '✓ Goal' : '✗ Goal'}
              </span>
            )}
            {onTrackStretch !== null && (
              <span className={`en-goal-badge en-goal-badge--stretch ${onTrackStretch ? 'en-goal-badge--on' : 'en-goal-badge--off'}`}>
                {onTrackStretch ? '✓ Stretch' : '✗ Stretch'}
              </span>
            )}
            <span className="en-chart-start">Start: {fmt(startValue)}</span>
          </div>
        </div>

        {/* legend — only when goal lines are present */}
        {(hasNormalGoal || hasStretchGoal) && (
          <div className="en-chart-legend">
            <span className="en-legend-item en-legend-equity" style={{ '--lc': color }}>Equity</span>
            {hasNormalGoal  && <span className="en-legend-item en-legend-goal">Goal pace</span>}
            {hasStretchGoal && <span className="en-legend-item en-legend-stretch">Stretch pace</span>}
          </div>
        )}

        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={data} margin={{ top: 8, right: 20, left: 10, bottom: 42 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#6b7280' }}
              angle={-35}
              textAnchor="end"
              interval="preserveStartEnd"
            />
            <YAxis
              tickFormatter={fmtAxis}
              tick={{ fontSize: 11, fill: '#6b7280' }}
              domain={[yMin, yMax]}
              tickCount={5}
              width={62}
            />
            <Tooltip
              formatter={tooltipFmt}
              contentStyle={{ fontSize: 12, borderRadius: 6 }}
            />
            {belowStart && (
              <ReferenceLine
                y={startValue}
                stroke="#f87171"
                strokeDasharray="4 4"
                label={{ value: 'Start', position: 'insideTopRight', fontSize: 10, fill: '#f87171' }}
              />
            )}
            <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2}
              dot={false} activeDot={{ r: 5 }} connectNulls={false} />
            {hasNormalGoal && (
              <Line type="linear" dataKey="normalGoal"
                stroke="rgba(245,158,11,0.5)" strokeWidth={1.5} strokeDasharray="6 3"
                dot={false} activeDot={{ r: 4 }} connectNulls />
            )}
            {hasStretchGoal && (
              <Line type="linear" dataKey="stretchGoal"
                stroke="rgba(139,92,246,0.5)" strokeWidth={1.5} strokeDasharray="6 3"
                dot={false} activeDot={{ r: 4 }} connectNulls />
            )}
          </LineChart>
        </ResponsiveContainer>

        {/* year-end target text */}
        {(yearEndNormal || yearEndStretch) && (
          <div className="en-chart-targets">
            Year-end targets:
            {yearEndNormal  && <span className="en-target-normal"> Goal {fmt(yearEndNormal)}</span>}
            {yearEndStretch && <span className="en-target-stretch"> · Stretch {fmt(yearEndStretch)}</span>}
          </div>
        )}
      </div>
    );
  };

  // ── render: equity tab ─────────────────────────────────────────────────────
  const renderEquity = () => {
    if (equityLoading) return <div className="en-loading">Loading equity data…</div>;
    if (error) return <div className="en-error">{error}</div>;
    const latest = groupedEquity[0];
    const prev = groupedEquity[1];
    return (
      <div>
        {latest && (
          <div className="en-summary-cards">
            {[
              { label: 'Cash', val: latest.cash, diff: prev ? latest.cash - prev.cash : null },
              { label: 'Stocks', val: latest.stocks, diff: latest.stocksDiff },
              { label: 'Options', val: latest.options, diff: latest.optionsDiff },
              { label: 'Total', val: latest.total, diff: latest.totalDiff },
            ].map(c => (
              <div key={c.label} className="en-card">
                <div className="en-card-label">{c.label}</div>
                <div className="en-card-value">{fmt(c.val)}</div>
                {c.diff !== null && <div className={`en-card-diff ${dc(c.diff)}`}>{fmtDiff(c.diff)}</div>}
              </div>
            ))}
          </div>
        )}
        <div className="en-charts-section">
          <div className="en-charts-grid">
            {renderChart(ibkrChart,  'IBKR — Year to Date',       '#667eea')}
            {renderChart(tastyChart, 'TastyTrade — Since Start', '#10b981')}
          </div>
        </div>
        <div className="en-table-wrap">
          <table className="en-table">
            <thead>
              <tr>
                <th>{groupBy === 'day' ? 'Day' : groupBy === 'week' ? 'Week' : 'Month'}</th>
                <th>Cash</th><th>Stocks</th><th>Stocks Δ</th>
                <th>Options</th><th>Options Δ</th><th>Total</th><th>Total Δ</th>
              </tr>
            </thead>
            <tbody>
              {groupedEquity.length === 0
                ? <tr><td colSpan={8} className="en-empty">No equity data</td></tr>
                : groupedEquity.map(r => (
                  <tr key={r.key}>
                    <td className="en-period">{r.label}</td>
                    <td>{fmt(r.cash)}</td>
                    <td>{fmt(r.stocks)}</td>
                    <td className={dc(r.stocksDiff)}>{fmtDiff(r.stocksDiff)}</td>
                    <td>{fmt(r.options)}</td>
                    <td className={dc(r.optionsDiff)}>{fmtDiff(r.optionsDiff)}</td>
                    <td className="en-total-col">{fmt(r.total)}</td>
                    <td className={dc(r.totalDiff)}>{fmtDiff(r.totalDiff)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── render: P&L table ──────────────────────────────────────────────────────
  const renderPnlTable = (rows, label, note) => {
    const total = rows.reduce((s, r) => s + r.pnl, 0);
    return (
      <div className="en-pnl-panel">
        <div className="en-pnl-panel-header">
          <span className="en-pnl-broker-label">{label}</span>
          <span className={`en-pnl-total ${dc(total)}`}>Total: {fmtDiff(total)}</span>
        </div>
        {note && <div className="en-pnl-note">{note}</div>}
        <div className="en-table-wrap">
          <table className="en-table">
            <thead>
              <tr>
                <th>{groupBy === 'day' ? 'Day' : groupBy === 'week' ? 'Week' : 'Month'}</th>
                <th>Realized P&amp;L</th>
                <th title="Running total of all realized P&L">Cumulative ①</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0
                ? <tr><td colSpan={3} className="en-empty">No P&amp;L data</td></tr>
                : rows.map(r => (
                  <tr key={r.key}>
                    <td className="en-period">{r.label}</td>
                    <td className={dc(r.pnl)}>{fmtDiff(r.pnl)}</td>
                    <td className={dc(r.cumulative)}>{fmtDiff(r.cumulative)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {rows.length > 0 && (
          <div className="en-pnl-footnote">① Cumulative = running total of all realized P&amp;L above.</div>
        )}
      </div>
    );
  };

  // ── render: P&L tab ────────────────────────────────────────────────────────
  const renderPnl = () => {
    if (pnlLoading) return <div className="en-loading">Loading P&amp;L data…</div>;
    const showIbkr = selectedBroker === 'all' || selectedBroker === 'ibkr';
    const showTasty = selectedBroker === 'all' || selectedBroker === 'tastytrade';
    return (
      <div className="en-pnl-grid">
        {showIbkr && renderPnlTable(ibkrPnl, 'IBKR', null)}
        {showTasty && renderPnlTable(
          tastyPnl, 'TastyTrade',
          'Only fully closed positions are shown. Partial closes (e.g. positions opened before the data window) are excluded.'
        )}
      </div>
    );
  };

  // ── main ───────────────────────────────────────────────────────────────────
  return (
    <div className="en-container">
      {controls}
      {submenu === 'equity' && renderEquity()}
      {submenu === 'pnl' && renderPnl()}
    </div>
  );
}

export default EquityNew;
